/**
 * Phase 6 — Maverick Assist Mode (engine)
 *
 * Pure, deterministic recommendation generator. Given the current workflow
 * state, returns a ranked list of grounded recommendations.
 *
 * Implementation rules:
 *   • Pure function — no side effects, no mutations, no clock reads beyond
 *     the injected `now` (defaults to Date.now()).
 *   • Every recommendation references a real record id and includes a
 *     specific, verifiable reason.
 *   • Actions are pre-built ExecutableAction objects routed through
 *     runAction(); we never bypass the existing execution runtime.
 *   • Five recommendation types only (the trimmed Phase 6 set):
 *       1. stalled_queue_item
 *       2. unowned_work
 *       3. handoff_without_followthrough
 *       4. linked_work_mismatch
 *       5. duplicate_attention_on_same_job
 *
 * Items deferred (will arrive with later phases — DO NOT add here):
 *   • queue_item_ready_but_unassigned (subsumed by unowned_work)
 *   • likely_next_step                (too speculative for assist mode)
 *   • suggest_chain_or_reroute        (autonomy territory)
 */

import type { NavigateFunction } from "react-router-dom";
import type { FollowUpTask } from "@/data/opportunityTypes";
import type { ActionQueueItem } from "@/data/actionQueue/types";
import type { AttentionItem } from "@/components/AttentionItemDetailModal";
import type { ExecutableAction } from "@/lib/actionRunner";
import type {
  MaverickRecommendation,
  MaverickRecommendationPriority,
} from "./assistTypes";
import {
  getEffectiveIntent,
  INTENT_LABELS,
} from "@/data/handoffActions";

export interface AssistEngineInputs {
  handoffs: FollowUpTask[];
  queueItems: ActionQueueItem[];
  /** AI-derived attention items — used only for the duplicate detector. */
  aiSignals?: AttentionItem[];
  /** Injected for testability. */
  now?: number;
  /** Used for navigation-style fallback actions. */
  navigate?: NavigateFunction;
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/** Configurable thresholds — explicit, not heuristic. */
const STALLED_QUEUE_THRESHOLD_MS = 2 * DAY;          // 48h open
const LINKED_MISMATCH_THRESHOLD_MS = 1 * DAY;        // 24h after handoff completed
const HANDOFF_FOLLOWTHROUGH_THRESHOLD_MS = 6 * HOUR; // 6h after completion w/ no chained queue work

/* ─── Priority ranking (high > medium > low; stable order otherwise) ─── */
const PRIORITY_RANK: Record<MaverickRecommendationPriority, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/* ─── Tiny helpers ─── */
const isOpen = (s: ActionQueueItem["status"]) => s === "open" || s === "in_progress";
const ms = (iso?: string) => (iso ? new Date(iso).getTime() : 0);

function navAction(label: string, route?: string): ExecutableAction {
  return { label, executionMode: "navigate", route };
}

/* ─── Generators (each returns ZERO or more recommendations) ─── */

function genStalledQueueItems(
  items: ActionQueueItem[],
  now: number,
): MaverickRecommendation[] {
  const out: MaverickRecommendation[] = [];
  for (const item of items) {
    if (!isOpen(item.status)) continue;
    const ageMs = now - ms(item.createdAt);
    if (ageMs < STALLED_QUEUE_THRESHOLD_MS) continue;

    const ageDays = Math.floor(ageMs / DAY);
    const route = item.jobId ? `/jobs/${item.jobId}` : undefined;
    const fingerprint = `stalled:${ageDays}d:${item.status}`;

    out.push({
      id: `stalled_queue_item:${item.id}:${fingerprint}`,
      type: "stalled_queue_item",
      title: `Open ${ageDays} days — needs a push`,
      summary: item.title,
      reason: `${ageDays === 1 ? "A day" : `${ageDays} days`} ${item.status === "in_progress" ? "in progress" : "without movement"}. Step in before it stalls further.`,
      recommendedAction: route ? "Open job" : "Open queue",
      actionKey: item.actionKey ?? "open_queue_item",
      action: navAction(route ? "Open job" : "Open queue", route),
      priority: ageDays >= 5 ? "high" : "medium",
      confidence: "high",
      sourceType: "queue_item",
      sourceId: item.id,
      relatedQueueItemId: item.id,
      relatedJobId: item.jobId,
      relatedHandoffId: item.linkedHandoffId ?? item.originHandoffId,
      isSafeToRun: false,
      stateFingerprint: fingerprint,
    });
  }
  return out;
}

function genUnownedWork(items: ActionQueueItem[]): MaverickRecommendation[] {
  const out: MaverickRecommendation[] = [];
  for (const item of items) {
    if (!isOpen(item.status)) continue;
    const hasOwner = !!(item.assignedToRoleKey || item.assignedToUserId);
    if (hasOwner) continue;

    const route = item.jobId ? `/jobs/${item.jobId}` : undefined;
    const fingerprint = `unowned:${item.priority}`;
    out.push({
      id: `unowned_work:${item.id}:${fingerprint}`,
      type: "unowned_work",
      title: "No one owns this yet",
      summary: item.title,
      reason: "Open work without an owner tends to drift. Assign it before it stalls.",
      recommendedAction: route ? "Open job to assign" : "Open queue",
      actionKey: "assign_owner",
      action: navAction(route ? "Open job" : "Open queue", route),
      priority: item.priority === "high" ? "high" : "medium",
      confidence: "high",
      sourceType: "queue_item",
      sourceId: item.id,
      relatedQueueItemId: item.id,
      relatedJobId: item.jobId,
      isSafeToRun: false,
      stateFingerprint: fingerprint,
    });
  }
  return out;
}

function genHandoffWithoutFollowthrough(
  handoffs: FollowUpTask[],
  items: ActionQueueItem[],
  now: number,
): MaverickRecommendation[] {
  const out: MaverickRecommendation[] = [];
  // Index queue items by linked/origin handoff for O(1) lookup.
  const linkedHandoffIds = new Set<string>();
  for (const q of items) {
    if (q.linkedHandoffId) linkedHandoffIds.add(q.linkedHandoffId);
    if (q.originHandoffId) linkedHandoffIds.add(q.originHandoffId);
  }

  for (const t of handoffs) {
    if (t.status !== "completed") continue;
    // If chaining already produced a downstream queue item, it's accounted for.
    if (t.linkedQueueItemId) continue;
    if (linkedHandoffIds.has(t.id)) continue;
    // Outcome that *expects* downstream work.
    if (t.completionOutcome === "completed_and_created_followup") continue;

    const completedMs = ms(t.completedAt ?? t.createdAt);
    const sinceMs = now - completedMs;
    if (sinceMs < HANDOFF_FOLLOWTHROUGH_THRESHOLD_MS) continue;

    const intent = getEffectiveIntent(t);
    const route = t.jobId ? `/jobs/${t.jobId}` : undefined;
    const fingerprint = `nofollow:${Math.floor(sinceMs / HOUR)}h`;

    out.push({
      id: `handoff_without_followthrough:${t.id}:${fingerprint}`,
      type: "handoff_without_followthrough",
      title: "Hand-off closed, but nothing followed",
      summary: `${t.customerName ?? "Hand-off"} — ${INTENT_LABELS[intent]}`,
      reason: `Closed ${Math.floor(sinceMs / HOUR)}h ago with no downstream work created. Confirm the next step didn't get missed.`,
      recommendedAction: route ? "Open job" : "Open inbox",
      actionKey: "review_handoff",
      action: navAction(route ? "Open job" : "Open inbox", route),
      priority: "medium",
      confidence: "medium",
      sourceType: "handoff",
      sourceId: t.id,
      relatedHandoffId: t.id,
      relatedJobId: t.jobId,
      isSafeToRun: false,
      stateFingerprint: fingerprint,
    });
  }
  return out;
}

function genLinkedWorkMismatch(
  handoffs: FollowUpTask[],
  items: ActionQueueItem[],
  now: number,
): MaverickRecommendation[] {
  const out: MaverickRecommendation[] = [];
  const itemsById = new Map(items.map((i) => [i.id, i]));

  for (const t of handoffs) {
    if (t.status !== "completed") continue;
    if (!t.linkedQueueItemId) continue;
    const linked = itemsById.get(t.linkedQueueItemId);
    if (!linked) continue;
    if (!isOpen(linked.status)) continue;

    const completedMs = ms(t.completedAt ?? t.createdAt);
    const sinceMs = now - completedMs;
    if (sinceMs < LINKED_MISMATCH_THRESHOLD_MS) continue;

    const route = t.jobId ? `/jobs/${t.jobId}` : linked.jobId ? `/jobs/${linked.jobId}` : undefined;
    const days = Math.floor(sinceMs / DAY);
    const fingerprint = `mismatch:${days}d:${linked.status}`;

    out.push({
      id: `linked_work_mismatch:${t.id}:${fingerprint}`,
      type: "linked_work_mismatch",
      title: "Hand-off done — linked work still open",
      summary: linked.title,
      reason: `Hand-off closed ${days} day${days === 1 ? "" : "s"} ago, but its linked queue item hasn't moved. Push it forward or reassign.`,
      recommendedAction: route ? "Open job" : "Open queue item",
      actionKey: "review_linked_queue",
      action: navAction(route ? "Open job" : "Open queue item", route),
      priority: days >= 3 ? "high" : "medium",
      confidence: "high",
      sourceType: "queue_item",
      sourceId: linked.id,
      relatedHandoffId: t.id,
      relatedQueueItemId: linked.id,
      relatedJobId: linked.jobId ?? t.jobId,
      isSafeToRun: false,
      stateFingerprint: fingerprint,
    });
  }
  return out;
}

function genDuplicateAttentionOnSameJob(
  handoffs: FollowUpTask[],
  items: ActionQueueItem[],
  aiSignals: AttentionItem[],
): MaverickRecommendation[] {
  // Count open attention surfaces per jobId across all three lanes.
  const perJob = new Map<string, { handoffs: number; queue: number; ai: number; sampleQueueId?: string; sampleHandoffId?: string }>();
  const bump = (jobId: string, lane: "handoffs" | "queue" | "ai", refId?: string) => {
    const cur = perJob.get(jobId) ?? { handoffs: 0, queue: 0, ai: 0 };
    cur[lane] += 1;
    if (lane === "queue" && !cur.sampleQueueId) cur.sampleQueueId = refId;
    if (lane === "handoffs" && !cur.sampleHandoffId) cur.sampleHandoffId = refId;
    perJob.set(jobId, cur);
  };

  for (const t of handoffs) {
    if (t.status === "completed") continue;
    if (t.jobId) bump(t.jobId, "handoffs", t.id);
  }
  for (const q of items) {
    if (!isOpen(q.status)) continue;
    if (q.jobId) bump(q.jobId, "queue", q.id);
  }
  for (const a of aiSignals) {
    if (a.jobId) bump(a.jobId, "ai");
  }

  const out: MaverickRecommendation[] = [];
  for (const [jobId, c] of perJob.entries()) {
    const total = c.handoffs + c.queue + c.ai;
    const lanesUsed = (c.handoffs > 0 ? 1 : 0) + (c.queue > 0 ? 1 : 0) + (c.ai > 0 ? 1 : 0);
    // Trigger only when the same job appears in 2+ lanes AND has 3+ items —
    // a single signal per lane is normal and not noise.
    if (lanesUsed < 2 || total < 3) continue;

    const lanes = [
      c.handoffs > 0 ? `${c.handoffs} hand-off${c.handoffs > 1 ? "s" : ""}` : null,
      c.queue > 0 ? `${c.queue} queue item${c.queue > 1 ? "s" : ""}` : null,
      c.ai > 0 ? `${c.ai} AI signal${c.ai > 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(", ");

    const fingerprint = `dup:${total}:${lanesUsed}`;
    out.push({
      id: `duplicate_attention_on_same_job:${jobId}:${fingerprint}`,
      type: "duplicate_attention_on_same_job",
      title: "One job, multiple open signals",
      summary: `Job ${jobId}`,
      reason: `${lanes} all open against this job. Consolidate under one owner to stop duplicate work.`,
      recommendedAction: "Open job",
      actionKey: "consolidate_attention",
      action: navAction("Open job", `/jobs/${jobId}`),
      priority: total >= 5 ? "high" : "medium",
      confidence: "medium",
      sourceType: "job",
      sourceId: jobId,
      relatedJobId: jobId,
      relatedQueueItemId: c.sampleQueueId,
      relatedHandoffId: c.sampleHandoffId,
      isSafeToRun: false,
      stateFingerprint: fingerprint,
    });
  }
  return out;
}

/* ─── Public entry point ─── */

export interface GenerateRecommendationsResult {
  /** All recommendations the engine produced, ranked. */
  all: MaverickRecommendation[];
}

export function generateRecommendations(
  inputs: AssistEngineInputs,
): GenerateRecommendationsResult {
  const now = inputs.now ?? Date.now();
  const handoffs = inputs.handoffs ?? [];
  const queueItems = inputs.queueItems ?? [];
  const aiSignals = inputs.aiSignals ?? [];

  const all: MaverickRecommendation[] = [
    ...genStalledQueueItems(queueItems, now),
    ...genUnownedWork(queueItems),
    ...genHandoffWithoutFollowthrough(handoffs, queueItems, now),
    ...genLinkedWorkMismatch(handoffs, queueItems, now),
    ...genDuplicateAttentionOnSameJob(handoffs, queueItems, aiSignals),
  ];

  // Stable rank: priority → confidence → sourceId.
  const CONF_RANK = { high: 0, medium: 1, low: 2 } as const;
  all.sort((a, b) => {
    const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (p !== 0) return p;
    const c = CONF_RANK[a.confidence] - CONF_RANK[b.confidence];
    if (c !== 0) return c;
    return a.sourceId.localeCompare(b.sourceId);
  });

  // De-duplicate by id (defensive — generators should not collide).
  const seen = new Set<string>();
  const dedup = all.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));

  return { all: dedup };
}

/** Stable dismissal key: `${type}:${sourceId}:${stateFingerprint}`. */
export function recommendationDismissalKey(r: MaverickRecommendation): string {
  return `${r.type}:${r.sourceId}:${r.stateFingerprint}`;
}
